import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppNoticeShell from './AppNoticeShell';
import ResumeNotice from './ResumeNotice';
import ManualNotice from './ManualNotice';
import { useAppNotice } from '../../context/AppNoticeContext';
import { useUser } from '../../context/UserContext';
import { withReturnTo, currentPath } from '../../utils/returnTo';
import openAppLink, { isExternalLink } from '../../utils/openAppLink';
import { justJoined } from '../../utils/onboardingModel';

/**
 * Renders the interrupting notice — modal on desktop, bottom sheet on mobile.
 * What to show (and every timing gate behind it) is decided in
 * AppNoticeContext / server-side; this is only the presentation.
 * docs/plans/2026-08-handoff-layout/09-app-notices.md
 *
 * The resume notice is the Portal surface (`tone="dark"`); an admin announcement is the ink
 * sheet (`tone="ink"`, design_handoff_app_notices) — the same ManualNotice the composer previews.
 */
export default function AppNoticeHost() {
  const navigate = useNavigate();
  const location = useLocation();
  const { interrupting, dismiss, act } = useAppNotice();
  const { profile } = useUser();

  if (!interrupting) return null;

  // The resume prompt points INTO the sign-up flow, so it never stands over it (`/onboard`), over
  // the introduction offer the flow lands on (`?welcome=1`), or over the editor it hands finished
  // accounts to. Nor for the rest of the session that finished sign-up: someone who just chose to go
  // in without a photo lands on nothing. It comes back on their next visit.
  const resume = interrupting.kind === 'resume';
  const inFlow = location.pathname.startsWith('/onboard') || location.pathname.startsWith('/portal/edit') || /(^|[?&])welcome=1(&|$)/.test(location.search) || justJoined();
  if (resume && inFlow) return null;

  const onPrimary = async () => {
    const href = await act();
    if (!href) return;
    // An announcement may point outside Portal; the resume prompt never does.
    if (isExternalLink(href)) { openAppLink(navigate, href); return; }
    // Carry where they were, so finishing the resume returns them here rather
    // than dumping them on the dashboard.
    navigate(withReturnTo(href, currentPath(location)));
  };

  return (
    <AppNoticeShell onClose={dismiss} tone={resume ? 'dark' : 'ink'}>
      {resume
        ? <ResumeNotice notice={interrupting} photoUrl={profile?.avatarUrl || profile?.faceUrl || null} onPrimary={onPrimary} onSecondary={dismiss} />
        : <ManualNotice notice={interrupting} onPrimary={onPrimary} onSecondary={dismiss} />}
    </AppNoticeShell>
  );
}
